import {
    Box,
    Button, Card,
    Chip, DialogActions,
    DialogContent, DialogTitle,
    Divider, FormControl, FormLabel, Grid, IconButton, Input,
    LinearProgress, List, ListItem, ListItemDecorator,
    Modal,
    ModalDialog, Radio, RadioGroup,
    Stack, Switch, Textarea,
    Typography,
    useTheme
} from "@mui/joy";
import React, {ReactNode, useEffect, useMemo, useRef, useState} from "react";
import {
    ChevronLeft, ChevronRight,
    ClockIcon,
    DotIcon,
    FlagIcon,
    XIcon
} from "lucide-react";
import TestCompletedComponent from "./TestCompletedComponent";
import {useUITheme} from "../../../hooks/useUITheme";
import {useTestRoom} from "../../../hooks/useTestRoom";
import {useAsync} from "../../../hooks/useAsyncResult";
import {useNavigate, useSearchParams} from "react-router-dom";
import ActionConfirmationDialog from "../../ActionConfirmationDialog";
import {createSession} from "../../../services/api/session";
import LoadingPageComponent from "../../Loading/LoadingPageComponent";
import {formatTimeFromSeconds} from "../../../utils/formatTime";
import PreTestInstructionsDialog from "../PreTestInstructionsDialog";
import TestSubmissionLoadingDialog from "../TestSubmissionLoadingDialog";
import {SectionName} from "../../../models/section";
import IeltsListeningTestRoomComponent from "./IELTS/section/IeltsListeningTestRoomComponent";
import {TestMode} from "../../../models/test";
import IeltsReadingTestRoomComponent from "./IELTS/section/IeltsReadingTestRoomComponent";
import IeltsWritingTestRoomComponent from "./IELTS/section/IeltsWritingTestRoomComponent";
import IeltsSpeakingTestRoomComponent from "./IELTS/section/IeltsSpeakingTestRoomComponent";
import {useTranslation} from "react-i18next";
import {useWakeLock} from "../../../hooks/useWakeLock";
import {defaultTestTime} from "../../../utils/defaultTestTime";
import LoadingError from "../../Error/LoadingError";


type TestRoomComponentProps = {
    open?: boolean,
    mode?: TestMode,
    section?: SectionName,
    onClose?: () => void,
}

const TestRoomComponent = (props: TestRoomComponentProps) => {
    useWakeLock(props.open)
    const uiTheme = useUITheme()
    const theme = useTheme()
    const testRoom = useTestRoom()
    const sessionLoaded = useRef(false)
    const [time, setTime] = useState<number>()
    const [canStart, setCanStart] = useState(true)
    const [searchParams] = useSearchParams()
    const [preTestInstructionsDialogVisible, setPreTestInstructionsDialogVisible] = useState(false)
    const createSessionResultAsync = useAsync(createSession, true, false, false)
    const [showConfirmActionDialog, setShowConfirmActionDialog] = useState(false)
    const {t} = useTranslation()
    const timeRef = useRef<number>(null)

    const QuestionGroupComponentMemoized = useMemo(() => {
            if (!createSessionResultAsync.isSuccess || !canStart) return null

            switch (props.section) {
                case "listening":
                    return (
                        <IeltsListeningTestRoomComponent
                            audioInstructionsUrl={
                                createSessionResultAsync.payload?.subsectionInstructions?.[testRoom.currentSubsectionNumber]
                            }
                        />
                    )
                case "reading":
                    return <IeltsReadingTestRoomComponent/>
                case "writing":
                    return <IeltsWritingTestRoomComponent/>
                case "speaking":
                    return (
                        <IeltsSpeakingTestRoomComponent
                            audioContent={createSessionResultAsync.payload?.audioContent}
                        />
                    )
                default:
                    return null
            }
        },
        [createSessionResultAsync.payload, canStart, testRoom.currentSubsectionNumber])

    const handleCreateSessionSuccess = () => {
        testRoom.setSessionId(createSessionResultAsync.payload?.sessionId)
        if (props.section !== "speaking") {
            testRoom.setTimerEnabled(props.mode === "mock")
            setTime(defaultTestTime[createSessionResultAsync.payload?.test.exam.type][props.section] || 0)
        }
        setPreTestInstructionsDialogVisible(!(JSON.parse(localStorage.getItem("skipPreTestInstructions")) || false))
        setCanStart(JSON.parse(localStorage.getItem("skipPreTestInstructions")) || false)
    }

    const handleClose = () => {
        props.onClose?.()
        testRoom.clearTimeouts()
    }

    const initializeTimer = () => {
        if (createSessionResultAsync.payload?.test)
            setTime(defaultTestTime[createSessionResultAsync.payload?.test.exam.type][props.section] || 0)
    }

    const loadSession = () => {
        sessionLoaded.current = true
        createSessionResultAsync.execute(props.section as SectionName, searchParams.get("testId"))

    }

    useEffect(() => {
        if (createSessionResultAsync.isSuccess)
            testRoom.setQuestions(createSessionResultAsync.payload?.questions)
    }, [createSessionResultAsync.isSuccess])

    useEffect(() => {
        if (!sessionLoaded.current)
            loadSession()
    }, [])

    useEffect(() => {
        if (createSessionResultAsync.isSuccess)
            handleCreateSessionSuccess()
    }, [createSessionResultAsync.isSuccess])

    useEffect(() => {
        if (testRoom.timerEnabled && canStart) {
            const intervalId = setInterval(() => {
                timeRef.current -= 1
                if (timeRef.current <= 0) {
                    testRoom.submit(null, true)
                    clearInterval(intervalId)
                }

                setTime(timeRef.current)
            }, 1000)
            return () => clearInterval(intervalId)
        } else initializeTimer()
    }, [testRoom.timerEnabled, canStart])

    useEffect(() => {
        timeRef.current = time
    }, [time])

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault()
            e.returnValue = ""
        }

        window.addEventListener("beforeunload", handleBeforeUnload)
        return () => window.removeEventListener("beforeunload", handleBeforeUnload)
    }, [])


    return <Modal open={props.open} onClose={handleClose}>
        <ModalDialog layout={"fullscreen"} variant="outlined" role="alertdialog"
                     sx={{...uiTheme.backgroundStyle, padding: 0, gap: 0}}>

            {!createSessionResultAsync.isSuccess && !createSessionResultAsync.loading &&
                <LoadingError label={"Test"} onRetry={loadSession}/>}

            {createSessionResultAsync.loading ?
                <LoadingPageComponent label={"Test"}/> :
                testRoom.testSubmitted ?
                    <TestCompletedComponent answered={testRoom.questionAttempts?.length}
                                            onGoBack={() => setShowConfirmActionDialog(true)}
                                            questions={testRoom.questions?.length}
                                            timeUsed={testRoom.questionAttempts?.map(value => value.timeTakenSecs).reduce((previousValue, currentValue) => previousValue + currentValue, 0)}/> :
                    testRoom.hasRequestedSubmit && testRoom.submitFailed ?
                        <LoadingError action={"submit"} label={t("examSessionLabel")} onRetry={testRoom.submit}/> :
                        <>

                            <Stack sx={uiTheme.topBarOverlayProps}>

                                <Stack gap={1.5} padding={1} direction={{md: "row"}} alignItems={"center"} paddingX={3}
                                       justifyContent={{xs: "space-between"}}>
                                    <Stack direction={"row"} gap={2} paddingTop={1.5} alignItems={"center"}
                                           justifyContent={"flex-start"}
                                           width={"100%"}>

                                        <IconButton variant={"plain"} onClick={() => setShowConfirmActionDialog(true)}>
                                            <XIcon/>
                                        </IconButton>

                                        <Typography level={"h2"} textTransform={"capitalize"}>
                                            {createSessionResultAsync.payload?.test.exam.type.toUpperCase()} {props.section}
                                        </Typography>
                                    </Stack>

                                    <Stack width={"100%"} direction={"row"} gap={3} alignItems={"center"}
                                           justifyContent={{xs: "space-between", md: "flex-end"}}>

                                        {props.section !== "speaking" &&
                                            <Stack gap={0.5}>
                                                <Chip size={"lg"} variant={"outlined"} disabled={!testRoom.timerEnabled}
                                                      startDecorator={<ClockIcon
                                                          style={{color: theme.palette.secondary['400']}}/>}
                                                      sx={{paddingX: 3, paddingY: 1}}>
                                                    {formatTimeFromSeconds(time || 0)}
                                                </Chip>

                                                {props.mode !== "mock" &&
                                                    <Typography endDecorator={<Switch
                                                        color={testRoom.timerEnabled ? "secondary" : "neutral"}
                                                        checked={testRoom.timerEnabled}
                                                        onChange={event => testRoom.setTimerEnabled(event.target.checked)}/>}>
                                                        {t("toggleTimerLabel", {action: testRoom.timerEnabled ? t("disableTimerActionLabel") : t("enableTimerActionLabel")})}
                                                    </Typography>}
                                            </Stack>}


                                        {props.section !== "speaking" &&
                                            <Button size={"lg"} onClick={() => testRoom.submit()}
                                                    disabled={testRoom.questionAttempts?.length !== testRoom.totalQuestions.current}>
                                                {t("submitTestActionButtonLabel")}
                                            </Button>}
                                    </Stack>
                                </Stack>

                                <Divider/>

                                <Stack direction={"row"} alignItems={"center"}>
                                    <Stack padding={1.5} width={"100%"} paddingX={4} gap={1}>
                                        <Stack direction={"row"} alignItems={"center"}>
                                            <Typography level={"body-sm"}>
                                                {t("testProgressLabel:", {
                                                    progress: testRoom.answeredQuestions,
                                                    total: testRoom.totalQuestions.current
                                                })}
                                            </Typography>

                                            <Typography level={"body-sm"}>
                                                {t("testProgressPercentageLabel", {percentage: Math.round(testRoom.answeredQuestions / testRoom.totalQuestions.current * 100)})}
                                            </Typography>
                                        </Stack>

                                        <LinearProgress determinate
                                                        value={testRoom.answeredQuestions / testRoom.totalQuestions.current * 100}/>
                                    </Stack>

                                    {props.section !== "speaking" &&
                                        <Stack alignSelf={"flex-end"} gap={1} direction={"row"} padding={2}>
                                            <IconButton onClick={testRoom.previousSubsection}
                                                        disabled={!testRoom.canGoToPreviousSubsection}
                                                        size={"sm"} variant={"solid"}>
                                                <ChevronLeft/>
                                            </IconButton>

                                            <IconButton onClick={testRoom.nextSubsection}
                                                        disabled={!testRoom.canNavigateSubsections || !testRoom.canGoToNextSubsection}
                                                        size={"sm"} variant={"solid"}>
                                                <ChevronRight/>
                                            </IconButton>
                                        </Stack>}
                                </Stack>

                                <Divider/>

                            </Stack>

                            <DialogContent sx={{paddingX: 2, gap: 3, overflow: "hidden"}}>

                                <Grid container spacing={2} marginY={1}>
                                    <Grid xs={12}>
                                        <Card data-no-animation data-no-glow
                                              sx={{padding: 3, height: "65dvh", overflowY: "auto"}}>

                                            {QuestionGroupComponentMemoized}

                                        </Card>
                                    </Grid>
                                </Grid>

                                <PreTestInstructionsDialog test={createSessionResultAsync.payload?.test || {}}
                                                           open={preTestInstructionsDialogVisible}
                                                           questionCount={testRoom.questions?.length}
                                                           onClose={() => {
                                                               setCanStart(true)
                                                               setPreTestInstructionsDialogVisible(false)
                                                           }}/>

                                <ActionConfirmationDialog open={showConfirmActionDialog}
                                                          onClose={() => setShowConfirmActionDialog(false)}
                                                          onConfirm={handleClose}
                                                          onCancel={() => setShowConfirmActionDialog(false)}
                                                          label={t("leaveTestLabel")}
                                                          message={t("leaveTestMessage")}/>

                                <TestSubmissionLoadingDialog open={testRoom.submitting}/>

                            </DialogContent>
                        </>}
        </ModalDialog>

    </Modal>
}

export default TestRoomComponent